/* -------------------------------------------------------------------------
 * Casanera | Signature custom JS
 * -------------------------------------------------------------------------
 * Project-specific storefront behavior. Kept outside theme.js so theme
 * updates do not overwrite custom work.
 * ------------------------------------------------------------------------- */

(function () {
  'use strict';

  /* =======================================================================
     Auto-close the quick-buy modal after add to cart
     -----------------------------------------------------------------------
     The cross-sell quick-buy modal stays open after adding a product, with
     no feedback. product-form dispatches a bubbling "variant:add" on success.
     If that event came from inside a <quick-buy-modal>, close that modal the
     same way the X button does (Modal.hide()). Adds from non-modal forms
     (single-variant cross-sell, PDP, sticky add-to-cart) have no
     quick-buy-modal ancestor, so they are left untouched.
     ======================================================================= */
  document.addEventListener('variant:add', function (event) {
    var modal = event.target
      && typeof event.target.closest === 'function'
      && event.target.closest('quick-buy-modal');

    if (modal && typeof modal.hide === 'function') {
      modal.hide();
    }
  });

  /* =======================================================================
     Signature Shortcuts - hide the scrolling bar when nothing overflows
     -----------------------------------------------------------------------
     The section renders its <progress-bar> whenever the layout is a carousel,
     but with few shortcuts the row fits on screen and the bar is meaningless.
     Overflow is a runtime measurement, so the bar is toggled here: hidden
     unless the list it tracks is actually wider than its visible box. Re-runs
     on resize, on load (images change the row width) and on section re-render
     in the theme editor.
     ======================================================================= */
  var SHORTCUT_SCROLLBAR_HIDDEN = 'signature-scrollbar-hidden';
  var shortcutScrollbarFrame = null;

  function updateShortcutScrollbars() {
    var bars = document.querySelectorAll('progress-bar.subcollections__scrollbar');

    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      var list = document.getElementById(bar.getAttribute('track-scroll-target'));

      if (!list) {
        continue;
      }

      bar.classList.toggle(SHORTCUT_SCROLLBAR_HIDDEN, list.scrollWidth - list.clientWidth <= 1);
    }
  }

  function queueShortcutScrollbars() {
    if (shortcutScrollbarFrame) {
      return;
    }

    shortcutScrollbarFrame = requestAnimationFrame(function () {
      shortcutScrollbarFrame = null;
      updateShortcutScrollbars();
    });
  }

  /* One-shot events measure directly; only the resize stream is coalesced through rAF, which is
     paused while the document is hidden. */
  updateShortcutScrollbars();
  document.addEventListener('DOMContentLoaded', updateShortcutScrollbars);
  window.addEventListener('load', updateShortcutScrollbars);
  document.addEventListener('shopify:section:load', updateShortcutScrollbars);
  window.addEventListener('resize', queueShortcutScrollbars);

  /* =======================================================================
     Cart discount field (ported from newer Stretch / Dharug)
     -----------------------------------------------------------------------
     Lets a customer apply a discount code from the cart drawer. Clean
     reimplementation of the newer theme's web components against the cart
     runtime this theme ALREADY ships (global fetchCart cache, Shopify.routes,
     and a <cart-drawer> that re-renders on "cart:refresh").

     Flow: read current applicable codes from /cart.js, merge with the entered
     code (or drop one when removing), POST { discount } to /cart/update.js.
       - success           -> dispatch "cart:refresh" (drawer re-renders totals)
       - rejected / failure -> dispatch "cart:discount:error" (reveals banner)
     The discount is applied server-side immediately, so it carries to checkout.
     ======================================================================= */
  (function () {
    if (window.customElements.get('cart-discount-field')) {
      return;
    }

    var cartRoot = function () {
      return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    };

    async function currentApplicableCodes() {
      var res = await fetch(cartRoot() + 'cart.js', {
        headers: { Accept: 'application/json' },
      });
      var cart = await res.json();
      return (cart.discount_codes || [])
        .filter(function (d) { return d.applicable; })
        .map(function (d) { return d.code.toLowerCase(); });
    }

    class AbstractCartDiscount extends HTMLElement {
      async toggleDiscount(event) {
        var target = event.currentTarget;
        target.setAttribute('aria-busy', 'true');

        var codes = await currentApplicableCodes();

        // Remove button: strip its own code from the applied set
        if (target.hasAttribute('discount-code')) {
          var toRemove = target.getAttribute('discount-code').toLowerCase();
          codes = codes.filter(function (c) { return c !== toRemove; });
        }

        var entered = (event.target.value || '').trim();
        var discount = (codes.length ? codes.join(',') + ',' : '') + entered;

        var response;
        try {
          response = await fetch(cartRoot() + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
            body: JSON.stringify({ discount: discount }),
          });
        } catch (err) {
          target.setAttribute('aria-busy', 'false');
          this.dispatchEvent(new CustomEvent('cart:discount:error', { bubbles: true }));
          return;
        }

        target.setAttribute('aria-busy', 'false');

        if (!response.ok) {
          this.dispatchEvent(new CustomEvent('cart:discount:error', { bubbles: true }));
          return;
        }

        var json = await response.json();
        var rejected = (json.discount_codes || []).some(function (d) {
          return d.applicable === false;
        });

        if (rejected) {
          this.dispatchEvent(new CustomEvent('cart:discount:error', { bubbles: true }));
          return;
        }

        this.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    }

    class CartDiscountField extends AbstractCartDiscount {
      connectedCallback() {
        // "change" bubbles from the inner text input up to this element
        this.addEventListener('change', this.toggleDiscount.bind(this));
      }
    }

    class CartDiscountRemoveButton extends AbstractCartDiscount {
      connectedCallback() {
        this.addEventListener('click', this.toggleDiscount.bind(this));
      }
    }

    class CartDiscountBanner extends HTMLElement {
      connectedCallback() {
        this._reveal = function () { this.hidden = false; }.bind(this);
        document.addEventListener('cart:discount:error', this._reveal);
      }
      disconnectedCallback() {
        document.removeEventListener('cart:discount:error', this._reveal);
      }
    }

    customElements.define('cart-discount-field', CartDiscountField);
    customElements.define('cart-discount-remove-button', CartDiscountRemoveButton);
    customElements.define('cart-discount-banner', CartDiscountBanner);
  })();

  /* =======================================================================
     Random promo banner (<random-promo>)
     -----------------------------------------------------------------------
     Collection pages are full-page cached by Shopify, so a server-side
     random banner would stay frozen until the cache expires. The
     signature-promo snippet renders every valid metaobject banner as an
     inert <template> (templates never download their media). On connect we
     clone exactly ONE random template into the DOM, so only one image/video
     is ever fetched, and it reshuffles on every page load.

     The element is display:contents (set inline in Liquid), so the cloned
     .product-list__promo stays a direct grid item of <product-list> and its
     row/column span vars keep working.
     ======================================================================= */
  (function () {
    if (window.customElements.get('random-promo')) {
      return;
    }

    class RandomPromo extends HTMLElement {
      connectedCallback() {
        if (this._rendered) {
          return;
        }

        var templates = this.querySelectorAll(':scope > template');
        if (!templates.length) {
          return;
        }

        var index = Math.floor(Math.random() * templates.length);
        this.appendChild(templates[index].content.cloneNode(true));
        this._rendered = true;
      }
    }

    customElements.define('random-promo', RandomPromo);
  })();

  /* =======================================================================
     Shoppable reels  (sections/signature-shoppable-reels.liquid)
     -----------------------------------------------------------------------
     Simple, robust centre carousel with an infinite loop. The middle reel is
     the active one (bigger + autoplay); the first reel starts centred so the
     previous reels peek on the left and the next reels on the right.

     The loop uses clone bands on each side (before | reals | after). Clones are
     non-interactive visual copies (poster instead of a live video, ids/modals
     stripped) so they are cheap and the wrap looks identical to the real reel.
     When a clone settles in the centre we jump instantly to its real twin, with
     transitions off so the enlarge animation does not replay. Only the centred
     real reel ever plays. Navigation: swipe, arrows, or clicking an adjacent
     reel (which never starts it, just steps towards it).
     ======================================================================= */
  (function () {
    if (window.customElements.get('shoppable-reels')) {
      return;
    }

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    class ShoppableReels extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.signature-reels__track');
        if (!this.track) {
          return;
        }

        this.reals = Array.prototype.slice.call(this.track.children);
        this.count = this.reals.length;
        this.progressBar = this.querySelector('[data-reels-progress]');
        if (this.progressBar) {
          this.progressBar.setAttribute('aria-valuemax', this.count);
        }
        this.looping = this.count >= 3;
        this.activeIndex = 0;
        this._settleTimer = null;
        this._jumping = false;

        if (this.looping) {
          this.classList.add('is-looping');
          this._cloneBands();
        }
        this.all = Array.prototype.slice.call(this.track.children);

        this.addEventListener('click', this._onClick.bind(this), true);
        this.track.addEventListener('scroll', this._onScroll.bind(this), { passive: true });
        window.addEventListener('resize', this._debounce(this._recenter.bind(this), 150));

        if (window.ResizeObserver) {
          this._resizeObserver = new ResizeObserver(this._debounce(this._recenter.bind(this), 120));
          this._resizeObserver.observe(this.track);
        }

        if (window.Shopify && window.Shopify.designMode) {
          this.addEventListener('shopify:block:select', function (event) {
            var index = this.reals.indexOf(event.target);
            if (index > -1) {
              this._center(this.reals[index], true);
              this._activate(index);
            }
          }.bind(this));
        }

        requestAnimationFrame(function () {
          this._center(this.reals[0], false);
          this._activate(0);
        }.bind(this));
      }

      disconnectedCallback() {
        if (this._resizeObserver) {
          this._resizeObserver.disconnect();
        }
      }

      /* ---- clone bands ----------------------------------------------- */
      _cloneBands() {
        var before = document.createDocumentFragment();
        var after = document.createDocumentFragment();
        this.reals.forEach(function (slide) {
          before.appendChild(this._clone(slide));
          after.appendChild(this._clone(slide));
        }.bind(this));
        this.track.insertBefore(before, this.reals[0]);
        this.track.appendChild(after);
      }

      _clone(slide) {
        var clone = slide.cloneNode(true);
        clone.classList.remove('is-selected');
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.style.pointerEvents = 'none';
        Array.prototype.forEach.call(clone.querySelectorAll('[id]'), function (el) {
          el.removeAttribute('id');
        });
        Array.prototype.forEach.call(clone.querySelectorAll('quick-buy-modal'), function (el) {
          el.remove();
        });
        var media = clone.querySelector('video-media');
        if (media) {
          var poster = media.querySelector('img');
          if (poster) {
            media.parentNode.replaceChild(poster.cloneNode(true), media);
          } else {
            media.remove();
          }
        }
        return clone;
      }

      /* ---- geometry -------------------------------------------------- */
      _cx(slide) {
        return slide.offsetLeft + slide.offsetWidth / 2;
      }

      _vc() {
        return this.track.scrollLeft + this.track.clientWidth / 2;
      }

      _closest() {
        var center = this._vc();
        var best = null;
        var bestDistance = Infinity;
        this.all.forEach(function (slide) {
          var distance = Math.abs(this._cx(slide) - center);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = slide;
          }
        }.bind(this));
        return best;
      }

      _center(slide, smooth) {
        if (!slide) {
          return;
        }
        this.track.scrollTo({
          left: this._cx(slide) - this.track.clientWidth / 2,
          behavior: smooth ? 'smooth' : 'auto'
        });
      }

      _recenter() {
        if (this.track.clientWidth) {
          this._center(this.reals[this.activeIndex], false);
        }
      }

      /* ---- scroll handling ------------------------------------------- */
      _onScroll() {
        var closest = this._closest();
        this._mark(closest);
        // Play the centred real reel as soon as it reaches the middle.
        if (closest && !closest.hasAttribute('data-clone')) {
          var index = parseInt(closest.getAttribute('data-index'), 10);
          if (index !== this.activeIndex) {
            this._activate(index);
          }
        }
        clearTimeout(this._settleTimer);
        this._settleTimer = setTimeout(this._settle.bind(this), 80);
      }

      _settle() {
        if (this._jumping) {
          return;
        }
        var current = this._closest();
        if (!current) {
          return;
        }
        var isClone = current.hasAttribute('data-clone');
        var index = isClone
          ? ((this.all.indexOf(current) % this.count) + this.count) % this.count
          : parseInt(current.getAttribute('data-index'), 10);

        // A clone settled in the centre: jump to its real twin (transitions off
        // so the enlarge animation does not replay at the wrap).
        if (this.looping && isClone) {
          var real = this.reals[index];
          if (real) {
            this._jumping = true;
            this.classList.add('is-repositioning');
            this._mark(real);
            this.track.scrollBy({ left: this._cx(real) - this._cx(current), behavior: 'auto' });
            void this.track.offsetWidth;
            this.classList.remove('is-repositioning');
            this._jumping = false;
          }
        }
        this._activate(index);
      }

      /* ---- active state ---------------------------------------------- */
      _mark(slide) {
        this.all.forEach(function (item) {
          item.classList.toggle('is-selected', item === slide);
        });
      }

      _activate(index) {
        if (isNaN(index)) {
          return;
        }
        this.activeIndex = index;
        this._mark(this.reals[index]);
        if (this.progressBar) {
          this.progressBar.setAttribute('aria-valuenow', index + 1);
        }
        if (reduceMotion) {
          return;
        }
        this.reals.forEach(function (slide, i) {
          var media = slide.querySelector('video-media');
          if (!media) {
            return;
          }
          window.customElements.upgrade(media);
          if (i === index) {
            media.play({ restart: false });
          } else {
            media.pause();
          }
        });
      }

      /* ---- navigation ------------------------------------------------ */
      _step(direction) {
        var target;
        if (direction > 0) {
          target = (this.looping && this.activeIndex === this.count - 1)
            ? this.all[this.count * 2]
            : this.reals[Math.min(this.activeIndex + 1, this.count - 1)];
        } else {
          target = (this.looping && this.activeIndex === 0)
            ? this.all[this.count - 1]
            : this.reals[Math.max(this.activeIndex - 1, 0)];
        }
        this._center(target, true);
      }

      /* ---- interaction ----------------------------------------------- */
      _onClick(event) {
        var target = event.target;
        if (!target || typeof target.closest !== 'function') {
          return;
        }
        var sound = target.closest('[data-reels-sound]');
        if (sound) {
          this._sound(sound, event);
          return;
        }
        if (target.closest('[data-reels-prev]')) {
          this._step(-1);
          return;
        }
        if (target.closest('[data-reels-next]')) {
          this._step(1);
          return;
        }
        if (target.closest('.signature-reels__product')) {
          return;
        }
        var slide = target.closest('.signature-reels__slide');
        if (!slide) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        if (slide.classList.contains('is-selected')) {
          return;
        }
        this._step(this._cx(slide) > this._vc() ? 1 : -1);
      }

      _sound(button, event) {
        event.preventDefault();
        event.stopPropagation();
        var slide = button.closest('.signature-reels__slide');
        var video = slide && slide.querySelector('video-media video');
        if (!video) {
          return;
        }
        video.muted = !video.muted;
        button.classList.toggle('is-muted', video.muted);
        button.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
      }

      _debounce(fn, wait) {
        var timer;
        return function () {
          clearTimeout(timer);
          timer = setTimeout(fn, wait);
        };
      }
    }

    window.customElements.define('shoppable-reels', ShoppableReels);
  })();

  /* -----------------------------------------------------------------------
   * Collection / search floating filter button: hide it once the product grid
   * ends, not only near the footer. The native SandwichVisibility shows the
   * button from the toolbar to the footer; this adds `.signature-past-products`
   * (hidden via signature-custom.css) as soon as the product-list bottom is
   * within the viewport, so the button leaves the screen when products end.
   * Applies on all breakpoints. The product-list is queried fresh every frame,
   * so it survives facet AJAX re-renders.
   * --------------------------------------------------------------------- */
  (function () {
    function init() {
      var btn = document.querySelector('collection-facets-floating-button');
      if (!btn || btn.__sigPastProductsInit) return;
      btn.__sigPastProductsInit = true;

      function productList() {
        return document.querySelector(
          '.shopify-section--main-collection product-list, .shopify-section--main-collection .product-list, .shopify-section--main-search product-list, .shopify-section--main-search .product-list'
        );
      }

      function update() {
        var pl = productList();
        if (!pl) {
          btn.classList.remove('signature-past-products');
          return;
        }
        var pastProducts = pl.getBoundingClientRect().bottom <= window.innerHeight;
        btn.classList.toggle('signature-past-products', pastProducts);
      }

      var ticking = false;
      function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          update();
          ticking = false;
        });
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    }

    if (document.readyState !== 'loading') init();
    else document.addEventListener('DOMContentLoaded', init);
    document.addEventListener('shopify:section:load', init);
  })();

  /* =======================================================================
     Product gallery (video-first, desktop) — nav on VIDEO slides
     -----------------------------------------------------------------------
     Scoped to galleries marked .product-gallery--video-nav (product page).
     The theme both hides the custom cursor AND disables click paging whenever
     the active slide is not an image (onMediaChange toggles `hidden`, and
     onCarouselClick returns early). So a gallery that lands on or returns to a
     video loses its desktop arrows and click-to-advance. Product-gallery videos
     have no controls, so on those galleries:
       1. re-show the arrow cursor while hovering a video slide, and
       2. re-enable click paging on video slides (prev/next by click x).
     Images stay handled by the theme (no double-advance); 3D models and other
     galleries (featured product, quick-buy) are untouched. Desktop pointer only.
     ======================================================================= */
  (function () {
    var FINE = 'screen and (pointer: fine)';

    function videoActive(carousel) {
      var cell = carousel && carousel.selectedCell;
      var type = cell ? cell.getAttribute('data-media-type') : null;
      return type === 'video' || type === 'external_video';
    }

    // 1. Keep the arrow cursor visible while hovering a video slide.
    document.addEventListener('pointermove', function (event) {
      if (!window.matchMedia(FINE).matches) return;
      var gallery = event.target.closest('product-gallery.product-gallery--video-nav');
      if (!gallery) return;
      var cursor = gallery.querySelector('.product-gallery__cursor');
      if (!cursor || !cursor.hasAttribute('hidden')) return;
      if (videoActive(gallery.querySelector('scroll-carousel.product-gallery__carousel'))) {
        cursor.removeAttribute('hidden');
      }
    });

    // 2. Re-enable click paging on video slides.
    document.addEventListener('click', function (event) {
      if (!window.matchMedia(FINE).matches) return;
      var gallery = event.target.closest('product-gallery.product-gallery--video-nav');
      if (!gallery) return;
      if (event.target.closest('button, a[href], open-lightbox-button')) return;

      var carousel = gallery.querySelector('scroll-carousel.product-gallery__carousel');
      if (!carousel || !videoActive(carousel)) return;

      var offsetX = event.clientX - carousel.getBoundingClientRect().left;
      if (offsetX > carousel.clientWidth / 2) {
        if (typeof carousel.next === 'function') carousel.next();
      } else {
        if (typeof carousel.previous === 'function') carousel.previous();
      }
    });
  })();

  /* =======================================================================
     Mobile product gallery — tap image to open zoom (lightbox)
     -----------------------------------------------------------------------
     On mobile the native zoom control is a small corner button. Make a tap
     anywhere on a gallery image open the lightbox, while a horizontal swipe
     still scrolls the carousel. A pointerdown/click distance guard tells a tap
     (open zoom) from a swipe/drag (ignored). Delegated on document so it keeps
     working after the gallery re-renders on variant change. Desktop is left to
     the theme's own per-image zoom button.
     ======================================================================= */
  (function () {
    var MOBILE = '(max-width: 999px)';
    var startX = 0;
    var startY = 0;

    document.addEventListener(
      'pointerdown',
      function (event) {
        startX = event.clientX;
        startY = event.clientY;
      },
      true
    );

    document.addEventListener('click', function (event) {
      if (!window.matchMedia(MOBILE).matches) {
        return;
      }

      var media = event.target.closest('.product-gallery__media');
      if (!media || media.dataset.mediaType !== 'image') {
        return;
      }

      // Let real controls and non-image media behave normally.
      if (event.target.closest('button, a, [role="button"], open-lightbox-button, model-viewer, video-media')) {
        return;
      }

      var gallery = media.closest('product-gallery');
      if (!gallery || !gallery.hasAttribute('allow-zoom')) {
        return;
      }

      // A swipe moves the pointer; a tap barely does.
      if (Math.hypot(event.clientX - startX, event.clientY - startY) > 10) {
        return;
      }

      // Trigger the lightbox opener that is actually visible on mobile.
      var opener = Array.prototype.slice
        .call(gallery.querySelectorAll('open-lightbox-button'))
        .find(function (button) {
          return button.offsetParent !== null;
        });
      if (!opener) {
        return;
      }

      (opener.querySelector('button') || opener).click();
    });
  })();

  /* =======================================================================
     Signature Reels  (sections/signature-reels.liquid)
     -----------------------------------------------------------------------
     Center carousel powered by Embla Carousel (vendored UMD assets loaded
     deferred by the section). Loop works like the live theme's Flickity
     wrapAround: Embla repositions slides with per-slide transforms — no
     clones, no DOM moves — so every video keeps its exact playback state
     across the wrap. When there are not enough slides to cover the wrap,
     Embla automatically falls back to a finite centered carousel.

     This element layers the reels behavior on top:
     - marks the centered cell with `is-selected` (Embla "select");
     - plays the centered video once the carousel settles, pauses the rest
       (each video keeps its playback position);
     - click on a side reel selects it instead of following its links;
     - play/pause + sound toggles per reel; progress bar in value mode;
     - previous()/next() so the theme's native carousel arrow buttons work
       against this element via aria-controls;
     - plays only while the section is in the viewport, and never when the
       visitor prefers reduced motion.
     ======================================================================= */
  (function () {
    if (window.customElements.get('signature-reels')) {
      return;
    }

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    class SignatureReels extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.sig-reels__viewport');
        this.track = this.querySelector('.sig-reels__track');
        if (!this.viewport || !this.track) {
          return;
        }

        this.cells = Array.prototype.slice.call(this.track.children);
        this.activeCell = this.cells[0] || null;
        this.inView = false;
        this.progressBar = this.querySelector('progress-bar');

        // Under reduced motion nothing autoplays: reflect it on the buttons.
        if (reduceMotion.matches) {
          this.cells.forEach(function (cell) {
            var playButton = cell.querySelector('[data-sig-reels-play]');
            if (playButton) {
              playButton.classList.add('is-paused');
            }
          });
        }

        // Capture phase: swallow clicks on side reels (select them instead of
        // following the product link). Embla already suppresses clicks that
        // end a drag.
        this.viewport.addEventListener('click', this._onCarouselClick.bind(this), true);
        this.addEventListener('click', this._onControlClick.bind(this));

        // Physical gesture direction tracking. Embla's location delta cannot
        // be used: crossing the loop's wrap point re-anchors the location by
        // ± the content length, flipping the sign. Capture-phase document
        // listeners record the release point BEFORE Embla's own (bubble)
        // handlers emit "pointerUp".
        this._onDragStart = this._onDragStart.bind(this);
        this._onDragMove = this._onDragMove.bind(this);
        this._onDragEnd = this._onDragEnd.bind(this);
        this.viewport.addEventListener('mousedown', this._onDragStart);
        this.viewport.addEventListener('touchstart', this._onDragStart, { passive: true });
        document.addEventListener('mouseup', this._onDragEnd, true);
        document.addEventListener('touchend', this._onDragEnd, true);

        // Safety net: media events don't bubble but they DO capture. Fast
        // multi-cell drags can interleave the async play/pause calls (the
        // theme's media player proxy is async), leaving a passed video
        // playing. Any "play" landing in a non-selected cell is paused on
        // the spot, synchronously.
        this.track.addEventListener('play', this._onAnyPlay.bind(this), true);

        this._onBlockSelect = this._onBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this._onBlockSelect);

        this._observer = new IntersectionObserver(this._onIntersect.bind(this), { threshold: 0.3 });
        this._observer.observe(this);

        this._initWhenReady(0);
      }

      disconnectedCallback() {
        if (this._observer) {
          this._observer.disconnect();
        }

        document.removeEventListener('shopify:block:select', this._onBlockSelect);
        document.removeEventListener('mouseup', this._onDragEnd, true);
        document.removeEventListener('touchend', this._onDragEnd, true);
        document.removeEventListener('mousemove', this._onDragMove, true);
        document.removeEventListener('touchmove', this._onDragMove, true);

        if (this.embla) {
          this.embla.destroy();
          this.embla = null;
        }
      }

      /* Embla loads deferred; poll for the global with setTimeout (NOT rAF —
         rAF freezes in background tabs and init would never run). */
      _initWhenReady(attempt) {
        if (window.EmblaCarousel) {
          this._init();
        } else if (attempt < 200) {
          setTimeout(this._initWhenReady.bind(this, attempt + 1), 50);
        }
      }

      _init() {
        if (this.embla || !this.isConnected) {
          return;
        }

        // No wheel-gestures plugin by design: navigation is pointer drag +
        // clicks only, exactly like the live theme's Flickity reels
        // (trackpad/mouse-wheel horizontal swiping must NOT move the
        // carousel).
        // containScroll must be false: the default "trimSnaps" would override
        // the center alignment at the edges when the loop cannot engage and
        // Embla falls back to a finite carousel.
        this.embla = window.EmblaCarousel(this.viewport, {
          loop: true,
          align: 'center',
          containScroll: false,
          skipSnaps: false
        });

        this.embla.on('select', this._onSelect.bind(this));
        // Safety net: "select" already starts playback (synced with the grow
        // animation); settle re-asserts it in case play was not possible yet.
        // play() is guarded by the [playing] attribute, so this never
        // restarts a running video.
        this.embla.on('settle', this._playActive.bind(this));
        this.embla.on('pointerUp', this._onPointerUp.bind(this));
        this._onSelect();
      }

      _onDragStart(event) {
        var x = event.touches ? event.touches[0].clientX : event.clientX;
        this._dragStartX = x;
        this._dragEndX = x;
        this._dragSamples = [{ x: x, t: performance.now() }];
        document.addEventListener('mousemove', this._onDragMove, true);
        document.addEventListener('touchmove', this._onDragMove, true);
      }

      _onDragMove(event) {
        var x = event.touches ? event.touches[0].clientX : event.clientX;
        this._dragSamples.push({ x: x, t: performance.now() });

        if (this._dragSamples.length > 8) {
          this._dragSamples.shift();
        }
      }

      _onDragEnd(event) {
        this._dragEndX = event.changedTouches ? event.changedTouches[0].clientX : event.clientX;

        if (this._dragSamples) {
          this._dragSamples.push({ x: this._dragEndX, t: performance.now() });
        }

        document.removeEventListener('mousemove', this._onDragMove, true);
        document.removeEventListener('touchmove', this._onDragMove, true);
      }

      _onAnyPlay(event) {
        var cell = event.target.closest ? event.target.closest('.sig-reels__cell') : null;

        if (cell && this.cells.indexOf(cell) !== -1 && cell !== this.activeCell) {
          event.target.pause();
        }
      }

      /* Release behavior, modeled on the live theme's Flickity reels:
         - The drag itself is free and 1:1 — you can drag across as many
           cards as you like, the track follows your finger.
         - On release, the carousel settles on the card NEAREST to where the
           finger let go. It NEVER travels back across cards you dragged
           past, and it never glides on past your finger (no multi-card
           momentum).
         - A clear flick at release adds exactly ONE card in the flick's
           direction (so a tiny fast flick advances, and flicking backwards
           cancels) — same idea as Flickity's resting-point selection, minus
           its occasional long velocity overshoots.
         The nearest snap comes from the engine's own scrollTarget (loop-safe
         wrap math), not from pointer bookkeeping. */
      _onPointerUp() {
        var FLICK_VELOCITY = 0.35; /* px per ms over the release window */

        var engine = this.embla.internalEngine();
        var count = this.embla.scrollSnapList().length;

        // Nearest card to the release point, measured in the DOM: the cell
        // whose visual center is closest to the screen center right now.
        // (Embla's scrollTarget cannot be used here — it measures against
        // the already velocity-projected target, not the release position.)
        var mid = window.innerWidth / 2;
        var nearest = 0;
        var best = Infinity;

        this.cells.forEach(function (cell, index) {
          var box = cell.getBoundingClientRect();
          var d = Math.abs(box.left + box.width / 2 - mid);

          if (d < best) {
            best = d;
            nearest = index;
          }
        });

        // Release velocity: last pointer sample against one ~60ms+ earlier.
        // Windows under 30ms are discarded — bunched timestamps (throttled
        // timers, jittery hardware) fake absurd velocities.
        var velocity = 0;
        var samples = this._dragSamples || [];

        if (samples.length > 1) {
          var last = samples[samples.length - 1];
          var ref = samples[samples.length - 2];

          for (var i = samples.length - 2; i >= 0; i--) {
            ref = samples[i];
            if (last.t - ref.t >= 60) {
              break;
            }
          }

          if (last.t - ref.t >= 30) {
            velocity = (last.x - ref.x) / (last.t - ref.t);
          }
        }

        var bonus = 0;

        if (Math.abs(velocity) > FLICK_VELOCITY) {
          bonus = velocity < 0 ? 1 : -1;

          if (document.dir === 'rtl') {
            bonus = -bonus;
          }
        }

        var target = ((nearest + bonus) % count + count) % count;

        // Always re-target: overrides Embla's velocity projection with the
        // nearest-card landing, then soften the settle (Flickity-like
        // spring: it eases into place instead of snapping).
        this.embla.scrollTo(target);
        engine.scrollBody.useDuration(24).useFriction(0.7);
      }

      /* The theme's <carousel-prev-button>/<carousel-next-button> call these
         through aria-controls. With loop there are no edges, so the buttons
         simply never disable. */
      previous() {
        if (this.embla) {
          this.embla.scrollPrev();
        }
      }

      next() {
        if (this.embla) {
          this.embla.scrollNext();
        }
      }

      _onSelect() {
        var index = this.embla.selectedScrollSnap();
        var cell = this.cells[index];

        if (this.progressBar) {
          this.progressBar.setAttribute('aria-valuemax', this.cells.length);
          this.progressBar.setAttribute('aria-valuenow', index + 1);
        }

        if (!cell || cell === this.activeCell) {
          return;
        }

        if (this.activeCell) {
          this.activeCell.classList.remove('is-selected');
          this._pauseCell(this.activeCell);
        }

        cell.classList.add('is-selected');
        this.activeCell = cell;

        // Start playback in the same frame as the `is-selected` grow
        // animation, not at settle — the reel grows and plays as one motion.
        this._playActive();
      }

      _onBlockSelect(event) {
        var index = this.cells.indexOf(event.target);

        if (index !== -1 && this.embla) {
          this.embla.scrollTo(index, true);
        }
      }

      _onIntersect(entries) {
        this.inView = entries[0].isIntersecting;

        if (this.inView) {
          this._playActive();
        } else if (this.activeCell) {
          this._pauseCell(this.activeCell);
        }
      }

      _playActive() {
        var cell = this.activeCell;

        // Sweep: enforce single playback before (re)starting the active one.
        // Catches videos left playing by interleaved async play/pause calls.
        this.cells.forEach(function (other) {
          if (other !== cell) {
            this._pauseCell(other);
          }
        }, this);

        if (!cell || !this.inView || reduceMotion.matches || cell.hasAttribute('data-user-paused')) {
          return;
        }

        var media = cell.querySelector('video-media');

        if (media) {
          media.play();
          this._setPausedState(cell, false);
        }

        // Warm the neighbors so the next swipe starts instantly. Videos load
        // with preload="metadata", so without this the first play() on a
        // reel pays a visible buffering beat on mobile networks.
        this._warmNeighbors();
      }

      /* Kick off buffering for the cells adjacent to the active one. The
         muted play() starts the fetch behind the poster (the poster only
         fades on the media element's own [loaded] attribute, which raw
         video.play() does not set), then pauses right away — the capture
         "play" safety net pauses it too, which is fine: the request is
         already in flight and the browser keeps buffering. */
      _warmNeighbors() {
        var count = this.cells.length;
        var index = this.cells.indexOf(this.activeCell);

        if (index === -1 || count < 2) {
          return;
        }

        this._warmCell(this.cells[(index + 1) % count]);

        if (count > 2) {
          this._warmCell(this.cells[(index - 1 + count) % count]);
        }
      }

      _warmCell(cell) {
        var video = cell && cell.querySelector('video-media video');

        // readyState >= 3 (HAVE_FUTURE_DATA): already buffered enough.
        if (!video || video.readyState >= 3) {
          return;
        }

        video.preload = 'auto';

        var promise = video.play();

        if (promise && promise.then) {
          promise.then(function () {
            // If this cell became the active one mid-warm-up, leave it alone.
            if (!cell.classList.contains('is-selected')) {
              video.pause();
            }
          }).catch(function () {});
        }
      }

      _pauseCell(cell) {
        var media = cell.querySelector('video-media');

        if (!media) {
          return;
        }

        // Pause the inner <video> synchronously first (the media element's
        // own pause() goes through an async player proxy and can lose races
        // against a queued play), then let the element API sync its state.
        var video = media.querySelector('video');

        if (video && !video.paused) {
          video.pause();
        }

        if (typeof media.pause === 'function') {
          media.pause();
        }
      }

      _onCarouselClick(event) {
        // The click that ends a drag always lands on the grabbed card (it
        // tracks the pointer 1:1), which by then is often no longer the
        // selected one — treating it as click-to-select would yank the
        // carousel back onto the start card after every drag. This handler
        // runs in capture BEFORE Embla's own post-drag click suppressor
        // (viewport is above the track), so it must step aside itself:
        // click-to-select is only for real taps.
        var travel = (typeof this._dragEndX === 'number' ? this._dragEndX : 0) - (typeof this._dragStartX === 'number' ? this._dragStartX : 0);

        if (Math.abs(travel) > 10) {
          return;
        }

        var cell = event.target.closest('.sig-reels__cell');
        var index = this.cells.indexOf(cell);

        if (index === -1 || cell.classList.contains('is-selected')) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (this.embla) {
          // With loop, Embla takes the shortest path to the target snap.
          this.embla.scrollTo(index);
        }
      }

      _onControlClick(event) {
        var playButton = event.target.closest('[data-sig-reels-play]');
        var soundButton = event.target.closest('[data-sig-reels-sound]');

        if (!playButton && !soundButton) {
          return;
        }

        var cell = event.target.closest('.sig-reels__cell');
        var video = cell && cell.querySelector('video-media video');

        if (!video) {
          return;
        }

        if (playButton) {
          if (video.paused) {
            // Explicit user intent: play directly, even under reduced motion
            // (only autoplay is disabled there).
            cell.removeAttribute('data-user-paused');
            cell.querySelector('video-media').play();
            this._setPausedState(cell, false);
          } else {
            cell.setAttribute('data-user-paused', '');
            this._pauseCell(cell);
            this._setPausedState(cell, true);
          }
        } else {
          video.muted = !video.muted;
          soundButton.classList.toggle('is-muted', video.muted);
        }
      }

      _setPausedState(cell, paused) {
        var playButton = cell.querySelector('[data-sig-reels-play]');

        if (playButton) {
          playButton.classList.toggle('is-paused', paused);
        }
      }
    }

    customElements.define('signature-reels', SignatureReels);
  })();
})();
